import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../firebase';
import { doc, setDoc, updateDoc } from 'firebase/firestore';
import { Logo } from '../components/Logo';

function Onboarding() {
  const navigate = useNavigate();
  const { currentUser, userData, refreshUserData } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const [setupData, setSetupData] = useState({
    // Library Information
    libraryName: '',
    address: '',
    phone: '',
    
    // Barcode Settings
    startingBarcode: 125864,
    barcodePrefix: '',
    
    // OPAC Settings
    opacUrl: '',
    opacUsername: '',
    opacPassword: '',
    
    // Content Filtering Preferences
    enableContentFiltering: true,
    flagOverGrade8: true,
    flagSexualContent: true,
    flagControversialTopics: true,
    flagViolence: false,
    flagLanguage: false,
    enableDuplicateChecking: true, // NEW - Add this line
    
    // Cataloging Preferences
    defaultLocation: 'Fiction',
    priceMarkup: 1.00,
    batchSize: 30
  });

  const locations = [
    'Non-Fiction', 'Fiction', 'MS', 'MS Fiction', 'MS Non-Fiction',
    'MS Quick', 'Graphic Novels', 'MS Graphic Novels', 'Easy', 'Spanish'
  ];

  const steps = [
    {
      title: 'Welcome to HEIMDL!',
      subtitle: 'Let\'s set up your library catalog system',
      component: 'welcome'
    },
    {
      title: 'Library Information',
      subtitle: 'Tell us about your library',
      component: 'library'
    },
    {
      title: 'Barcode Settings',
      subtitle: 'Configure your barcode system',
      component: 'barcodes'
    },
    {
      title: 'OPAC Integration',
      subtitle: 'Connect to your library system (optional)',
      component: 'opac'
    },
    {
      title: 'Content Filtering',
      subtitle: 'Set age-appropriate filtering preferences',
      component: 'filtering'
    },
    {
      title: 'Ready to Begin!',
      subtitle: 'Your library catalog system is configured',
      component: 'complete'
    }
  ];

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeOnboarding = async () => {
    setLoading(true);
    try {
      // Create settings document
      const settingsRef = doc(db, 'users', currentUser.uid, 'settings', 'preferences');
      await setDoc(settingsRef, {
        // Library Information
        libraryName: setupData.libraryName,
        address: setupData.address,
        phone: setupData.phone,
        
        // OPAC Connection
        opacUrl: setupData.opacUrl || '',
        opacUsername: setupData.opacUsername || '',
        opacPassword: setupData.opacPassword || '',
        
        // Content Filtering Settings
        enableContentFiltering: setupData.enableContentFiltering,
        flagOverGrade8: setupData.flagOverGrade8,
        flagSexualContent: setupData.flagSexualContent,
        flagControversialTopics: setupData.flagControversialTopics,
        flagViolence: setupData.flagViolence,
        flagLanguage: setupData.flagLanguage,
        enableDuplicateChecking: setupData.enableDuplicateChecking, // NEW - Add this line
        
        // Cataloging Defaults
        defaultLocation: setupData.defaultLocation,
        priceMarkup: setupData.priceMarkup,
        batchSize: setupData.batchSize,
        
        // Barcode Management
        barcodePrefix: setupData.barcodePrefix || '',
        skipBarcodes: '',
        barcodeIncrement: 1,
        
        // System
        autoSave: true,
        enableNotifications: true,
        
        // Metadata
        createdAt: new Date(),
        lastUpdated: new Date(),
        onboardingCompleted: true
      });

      // Update user document with starting barcode and library name
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        lastBarcodeUsed: setupData.startingBarcode - 1, // So next book gets startingBarcode
        libraryName: setupData.libraryName,
        onboardingCompleted: true
      });

      // Navigate to Asgard
      navigate('/asgard');
    } catch (error) {
      console.error('Error completing onboarding:', error);
      alert('Error completing setup: ' + error.message);
    }
    setLoading(false);
  };

  const renderStepContent = () => {
    const step = steps[currentStep - 1];
    
    switch (step.component) {
      case 'welcome':
        return (
          <div className="text-center space-y-6">
            <Logo size={80} showText={false} />
            <div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">
                Welcome to HEIMDL, {userData?.schoolName}!
              </h2>
              <p className="text-slate-600">
                Let's set up your AI-powered library cataloging system in just a few steps.
              </p>
            </div>
            <div className="bg-gradient-to-r from-purple-100 to-indigo-100 rounded-lg p-6 border border-purple-200">
              <h3 className="font-bold text-purple-800 mb-3">What we'll configure:</h3>
              <ul className="text-sm text-purple-700 space-y-2 text-left">
                <li>📚 Your library information and preferences</li>
                <li>🏷️ Barcode numbering system</li>
                <li>🔗 LibraryWorld OPAC connection (optional)</li>
                <li>🛡️ Age-appropriate content filtering</li>
                <li>⚙️ Cataloging defaults and workflow settings</li>
              </ul>
            </div>
            <p className="text-sm text-slate-500">
              This should take about 3-5 minutes to complete.
            </p>
          </div>
        );

      case 'library':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Library Name *
              </label>
              <input
                type="text"
                required
                className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                placeholder="Holy Family Library"
                value={setupData.libraryName}
                onChange={(e) => setSetupData({...setupData, libraryName: e.target.value})}
              />
              <p className="text-xs text-slate-500 mt-1">This will appear on your barcode labels</p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Library Address (Optional)
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                placeholder="123 School Lane, City, State 12345"
                value={setupData.address}
                onChange={(e) => setSetupData({...setupData, address: e.target.value})}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Phone Number (Optional)
              </label>
              <input
                type="tel"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                placeholder="(555) 123-4567"
                value={setupData.phone}
                onChange={(e) => setSetupData({...setupData, phone: e.target.value})}
              />
            </div>
          </div>
        );

      case 'barcodes':
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Starting Barcode Number *
              </label>
              <input
                type="number"
                required
                min="1"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white font-mono text-lg"
                value={setupData.startingBarcode}
                onChange={(e) => setSetupData({...setupData, startingBarcode: parseInt(e.target.value) || 125864})}
              />
              <p className="text-xs text-slate-500 mt-1">
                Your first book will use this barcode number. Choose carefully - this can't be easily changed later.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Barcode Prefix (Optional)
              </label>
              <input
                type="text"
                maxLength="6"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                placeholder="e.g., HFCS"
                value={setupData.barcodePrefix}
                onChange={(e) => setSetupData({...setupData, barcodePrefix: e.target.value.toUpperCase()})}
              />
              <p className="text-xs text-slate-500 mt-1">
                {setupData.barcodePrefix 
                  ? `Your barcodes will look like: ${setupData.barcodePrefix}${setupData.startingBarcode}`
                  : `Your barcodes will be numeric: ${setupData.startingBarcode}`
                }
              </p>
            </div>

            <div className="bg-blue-100 rounded-lg p-4 border border-blue-200">
              <h4 className="font-medium text-blue-800 mb-2">💡 Barcode Tips:</h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Use consecutive numbering for easy inventory management</li>
                <li>• Consider starting at a round number like 130000</li>
                <li>• Prefixes help identify your books vs. borrowed materials</li>
                <li>• Standard practice: 6-digit numbers are typical for school libraries</li>
              </ul>
            </div>
          </div>
        );

      case 'opac':
        return (
          <div className="space-y-4">
            <div className="bg-yellow-100 rounded-lg p-4 border border-yellow-200">
              <h4 className="font-medium text-yellow-800 mb-2">🔗 OPAC Integration (Optional)</h4>
              <p className="text-sm text-yellow-700">
                Connect to your existing library system to automatically check for duplicate books during cataloging.
                Books flagged as potential duplicates will be sent to Loki's Chamber for your review.
                You can skip this and set it up later in Settings.
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                LibraryWorld URL
              </label>
              <input
                type="url"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                placeholder="https://yourschool.libraryworld.com"
                value={setupData.opacUrl}
                onChange={(e) => setSetupData({...setupData, opacUrl: e.target.value})}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Username
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                placeholder="librarian"
                value={setupData.opacUsername}
                onChange={(e) => setSetupData({...setupData, opacUsername: e.target.value})}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Password
              </label>
              <input
                type="password"
                className="w-full px-3 py-2 border border-slate-300 rounded-lg bg-white"
                placeholder="Optional - for duplicate checking"
                value={setupData.opacPassword}
                onChange={(e) => setSetupData({...setupData, opacPassword: e.target.value})}
              />
              <p className="text-xs text-slate-500 mt-1">
                Used to automatically check for duplicate books before cataloging
              </p>
            </div>
          </div>
        );

      case 'filtering':
        return (
          <div className="space-y-4">
            <div className="bg-purple-100 rounded-lg p-4 border border-purple-200">
              <h4 className="font-medium text-purple-800 mb-2">🛡️ Content Filtering for PreK-8</h4>
              <p className="text-sm text-purple-700">
                HEIMDL's AI analyzes books for age-appropriate content. Flagged items go to Loki's Chamber for your review.
              </p>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-slate-700">Enable Content Filtering</h4>
                <p className="text-sm text-slate-500">Master toggle for AI content analysis</p>
              </div>
              <input
                type="checkbox"
                checked={setupData.enableContentFiltering}
                onChange={(e) => setSetupData({...setupData, enableContentFiltering: e.target.checked})}
                className="h-5 w-5 text-purple-600"
              />
            </div>

            {setupData.enableContentFiltering && (
              <div className="space-y-3 pl-4 border-l-2 border-purple-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-medium text-slate-700">Over Grade 8 Content</h5>
                    <p className="text-xs text-slate-500">High school/adult level books</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={setupData.flagOverGrade8}
                    onChange={(e) => setSetupData({...setupData, flagOverGrade8: e.target.checked})}
                    className="h-4 w-4 text-red-600"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-medium text-slate-700">Sexual/Romantic Content</h5>
                    <p className="text-xs text-slate-500">Romance, dating, sexual themes</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={setupData.flagSexualContent}
                    onChange={(e) => setSetupData({...setupData, flagSexualContent: e.target.checked})}
                    className="h-4 w-4 text-pink-600"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-medium text-slate-700">Controversial Topics</h5>
                    <p className="text-xs text-slate-500">Sensitive social/political content</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={setupData.flagControversialTopics}
                    onChange={(e) => setSetupData({...setupData, flagControversialTopics: e.target.checked})}
                    className="h-4 w-4 text-orange-600"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-medium text-slate-700">Violence & Weapons</h5>
                    <p className="text-xs text-slate-500">Fighting, war, weapons</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={setupData.flagViolence}
                    onChange={(e) => setSetupData({...setupData, flagViolence: e.target.checked})}
                    className="h-4 w-4 text-red-600"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-medium text-slate-700">Language Concerns</h5>
                    <p className="text-xs text-slate-500">Profanity, inappropriate language</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={setupData.flagLanguage}
                    onChange={(e) => setSetupData({...setupData, flagLanguage: e.target.checked})}
                    className="h-4 w-4 text-yellow-600"
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <h5 className="font-medium text-slate-700">OPAC Duplicate Detection</h5>
                    <p className="text-xs text-slate-500">Check existing catalog before adding books</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={setupData.enableDuplicateChecking}
                    onChange={(e) => setSetupData({...setupData, enableDuplicateChecking: e.target.checked})}
                    className="h-4 w-4 text-blue-600"
                  />
                </div>
              </div>
            )}

            <div className="bg-green-100 rounded-lg p-3 border border-green-200">
              <p className="text-sm text-green-700">
                <strong>Recommended:</strong> Enable content filtering and duplicate detection for most PreK-8 Catholic schools.
                You can always adjust these settings later in Yggdrasil.
              </p>
            </div>
          </div>
        );

      case 'complete':
        return (
          <div className="text-center space-y-6">
            <div className="text-6xl">🎉</div>
            <div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">
                Welcome to Asgard!
              </h2>
              <p className="text-slate-600">
                Your HEIMDL system is now configured and ready to begin cataloging books.
              </p>
            </div>
            
            <div className="bg-gradient-to-r from-emerald-100 to-blue-100 rounded-lg p-6 border border-emerald-200">
              <h3 className="font-bold text-emerald-800 mb-3">Your Setup Summary:</h3>
              <div className="text-sm text-emerald-700 space-y-1 text-left">
                <p><strong>Library:</strong> {setupData.libraryName}</p>
                <p><strong>Starting Barcode:</strong> {setupData.barcodePrefix}{setupData.startingBarcode}</p>
                <p><strong>Content Filtering:</strong> {setupData.enableContentFiltering ? 'Enabled' : 'Disabled'}</p>
                <p><strong>OPAC:</strong> {setupData.opacUrl ? 'Connected' : 'Not configured'}</p>
              </div>
            </div>
            
            <div className="bg-purple-100 rounded-lg p-4 border border-purple-200">
              <h4 className="font-medium text-purple-800 mb-2">🚀 Next Steps:</h4>
              <ol className="text-sm text-purple-700 space-y-1 text-left">
                <li>1. Visit <strong>The Forge of Odin</strong> to start cataloging books</li>
                <li>2. Use <strong>The Rune Printer</strong> to create barcode labels</li>
                <li>3. Export MARC records via <strong>Bifrost Console</strong></li>
                <li>4. Manage settings anytime in <strong>Yggdrasil</strong></li>
              </ol>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 2: // Library step
        return setupData.libraryName.trim() !== '';
      case 3: // Barcode step
        return setupData.startingBarcode > 0;
      default:
        return true;
    }
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-900 to-indigo-900 text-white shadow-xl border-b border-purple-700/50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Logo size={40} showText={false} />
              <h1 className="text-lg sm:text-2xl font-bold tracking-wider">HEIMDL Setup</h1>
            </div>
            <div className="text-right">
              <p className="text-sm text-purple-300">Step {currentStep} of {steps.length}</p>
              <p className="text-lg font-bold">{Math.round((currentStep / steps.length) * 100)}% Complete</p>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-4 py-2">
          <div className="w-full bg-slate-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-purple-600 to-indigo-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / steps.length) * 100}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-4 sm:py-6 md:py-8">
        <div className="bg-white rounded-xl shadow-xl p-4 sm:p-6 md:p-8 border border-slate-200">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-slate-800 mb-2">
              {steps[currentStep - 1].title}
            </h2>
            <p className="text-slate-600">
              {steps[currentStep - 1].subtitle}
            </p>
          </div>

          <div className="mb-8">
            {renderStepContent()}
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className="bg-slate-500 text-white px-6 py-2 rounded-lg hover:bg-slate-600 disabled:bg-slate-300 disabled:cursor-not-allowed"
            >
              ← Previous
            </button>

            {currentStep === steps.length ? (
              <button
                onClick={completeOnboarding}
                disabled={loading}
                className="bg-green-600 text-white px-8 py-2 rounded-lg hover:bg-green-700 disabled:bg-slate-400 font-bold"
              >
                {loading ? 'Setting up...' : 'Complete Setup & Enter Asgard →'}
              </button>
            ) : (
              <button
                onClick={nextStep}
                disabled={!canProceed()}
                className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 disabled:bg-slate-400"
              >
                Next →
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default Onboarding;